import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Upload,
  Download,
  Car,
  Shield,
  Heart,
  Award,
  CreditCard,
  RefreshCw,
  Edit,
  Eye,
  History,
  ClipboardCheck
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { formatDateShort } from '../lib/utils';
import { toast } from 'sonner';
import HabilitationDocuments from '../components/HabilitationDocuments';
import DocumentPreviewDialog from '../components/DocumentPreviewDialog';
import { buildHabilitationDocPreview, downloadHabilitationDocument } from '../lib/habilitationDocuments';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SUIVI_LABELS = {
  a_traiter: 'À traiter',
  en_cours: 'Action en cours',
  renouvelee: 'Renouvelée'
};

const HISTORY_MOTIF_LABELS = {
  renouvellement: 'Renouvellement',
  modification: 'Modification des dates'
};

const EMPTY_RENEW_FORM = { date_obtention: '', date_expiration: '', organisme: '', commentaire: '' };
const EMPTY_SUIVI_FORM = { suivi_statut: 'a_traiter', suivi_date_prevue: '', suivi_commentaire: '' };

const DEFAULT_TYPE_ICONS = {
  visite_medicale: { icon: Heart, color: 'text-red-500' },
  sst: { icon: Shield, color: 'text-amber-500' },
  habilitation_electrique: { icon: Shield, color: 'text-amber-500' },
  permis: { icon: Car, color: 'text-blue-500' },
  formation_securite: { icon: Award, color: 'text-emerald-500' },
  certification_metier: { icon: Award, color: 'text-emerald-500' },
  autorisation_travail: { icon: CreditCard, color: 'text-purple-500' },
  attestation_formation: { icon: FileText, color: 'text-slate-500' },
};

const permisCategories = ['B', 'C', 'CE', 'C1', 'C1E', 'D', 'DE'];

const Habilitations = () => {
  const [habilitations, setHabilitations] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [habilitationTypes, setHabilitationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedHabForDoc, setSelectedHabForDoc] = useState(null);
  const [isDocDialogOpen, setIsDocDialogOpen] = useState(false);

  // Suivi du renouvellement (A-5) et aperçu (A-6)
  const [filterSuivi, setFilterSuivi] = useState('all');
  const [renewHab, setRenewHab] = useState(null);
  const [renewForm, setRenewForm] = useState(EMPTY_RENEW_FORM);
  const [renewFile, setRenewFile] = useState(null);
  const [renewSaving, setRenewSaving] = useState(false);
  const [suiviHab, setSuiviHab] = useState(null);
  const [suiviForm, setSuiviForm] = useState(EMPTY_SUIVI_FORM);
  const [historyHab, setHistoryHab] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  const [editingHabilitation, setEditingHabilitation] = useState(null);

  const [formData, setFormData] = useState({
    employee_id: '',
    type_habilitation: '',
    numero: '',
    date_obtention: '',
    date_expiration: '',
    organisme: '',
    categorie: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const buildTypesArray = (typesObj) => {
    return Object.entries(typesObj).map(([code, info]) => {
      const defaultIcon = DEFAULT_TYPE_ICONS[code] || { icon: FileText, color: 'text-slate-500' };
      return {
        value: code,
        label: info.label || code,
        alert_days: info.alert_days || 90,
        icon: defaultIcon.icon,
        color: defaultIcon.color,
      };
    });
  };

  const fetchData = async () => {
    try {
      const [habRes, alertsRes, empRes, typesRes] = await Promise.all([
        axios.get(`${API_URL}/habilitations`),
        axios.get(`${API_URL}/habilitations/alerts`),
        axios.get(`${API_URL}/employees`),
        axios.get(`${API_URL}/admin/habilitation-types`)
      ]);
      setHabilitations(habRes.data);
      setAlerts(alertsRes.data);
      setEmployees(empRes.data);
      setHabilitationTypes(buildTypesArray(typesRes.data));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingHabilitation) {
        await axios.put(`${API_URL}/habilitations/${editingHabilitation.id}`, formData);
        toast.success('Habilitation mise à jour');
      } else {
        await axios.post(`${API_URL}/habilitations`, formData);
        toast.success('Habilitation créée');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving habilitation:', error);
      toast.error(editingHabilitation ? 'Erreur lors de la mise à jour' : 'Erreur lors de la création');
    }
  };

  const handleEdit = (hab) => {
    setEditingHabilitation(hab);
    setFormData({
      employee_id: hab.employee_id || '',
      type_habilitation: hab.type_habilitation || '',
      numero: hab.numero || '',
      date_obtention: hab.date_obtention || '',
      date_expiration: hab.date_expiration || '',
      organisme: hab.organisme || '',
      categorie: hab.categorie || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette habilitation ?')) return;
    try {
      await axios.delete(`${API_URL}/habilitations/${id}`);
      toast.success('Habilitation supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setEditingHabilitation(null);
    setFormData({
      employee_id: '',
      type_habilitation: '',
      numero: '',
      date_obtention: '',
      date_expiration: '',
      organisme: '',
      categorie: ''
    });
  };

  // ---- Renouvellement ----
  const openRenew = (hab) => {
    setRenewHab(hab);
    setRenewForm({ ...EMPTY_RENEW_FORM, organisme: hab.organisme || '' });
    setRenewFile(null);
  };

  const handleRenewFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) { setRenewFile(null); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Fichier trop volumineux (max 10MB)'); return; }
    if (!['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      toast.error('Format non supporté. Utilisez PDF, JPG ou PNG');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const ext = file.name.split('.').pop().toLowerCase();
      setRenewFile({ filename: file.name, content_base64: reader.result.split(',')[1], file_type: ext === 'jpeg' ? 'jpg' : ext });
    };
    reader.readAsDataURL(file);
  };

  const handleRenew = async (e) => {
    e.preventDefault();
    if (!renewHab) return;
    if (!renewForm.date_obtention || !renewForm.date_expiration) {
      toast.error('Les nouvelles dates sont obligatoires');
      return;
    }
    setRenewSaving(true);
    try {
      await axios.post(`${API_URL}/habilitations/${renewHab.id}/renew`, {
        date_obtention: renewForm.date_obtention,
        date_expiration: renewForm.date_expiration,
        organisme: renewForm.organisme || null,
        commentaire: renewForm.commentaire || null
      });
      if (renewFile) {
        try {
          await axios.post(`${API_URL}/habilitations/${renewHab.id}/documents`, {
            habilitation_id: renewHab.id,
            employee_id: renewHab.employee_id,
            ...renewFile
          });
        } catch (docError) {
          console.error('Error uploading renewal document:', docError);
          toast.error('Habilitation renouvelée mais le document n\'a pas pu être joint');
        }
      }
      toast.success('Habilitation renouvelée');
      setRenewHab(null);
      fetchData();
    } catch (error) {
      console.error('Error renewing habilitation:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du renouvellement');
    } finally {
      setRenewSaving(false);
    }
  };

  // ---- Suivi ----
  const openSuivi = (hab) => {
    setSuiviHab(hab);
    setSuiviForm({
      suivi_statut: hab.suivi_statut && hab.suivi_statut !== 'renouvelee' ? hab.suivi_statut : 'a_traiter',
      suivi_date_prevue: hab.suivi_date_prevue || '',
      suivi_commentaire: hab.suivi_commentaire || ''
    });
  };

  const handleSuivi = async (e) => {
    e.preventDefault();
    if (!suiviHab) return;
    try {
      await axios.put(`${API_URL}/habilitations/${suiviHab.id}/suivi`, {
        suivi_statut: suiviForm.suivi_statut,
        suivi_date_prevue: suiviForm.suivi_date_prevue || null,
        suivi_commentaire: suiviForm.suivi_commentaire || null
      });
      toast.success('Suivi mis à jour');
      setSuiviHab(null);
      fetchData();
    } catch (error) {
      console.error('Error updating suivi:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour du suivi');
    }
  };

  // ---- Aperçu du dernier document (A-6) ----
  const handlePreviewRow = async (hab) => {
    if (!hab.dernier_document_id) return;
    try {
      const preview = await buildHabilitationDocPreview({ id: hab.dernier_document_id, filename: '' });
      if (!preview) { toast.error('Aucune donnée disponible pour ce document'); return; }
      setPreviewDoc(preview);
    } catch (error) {
      console.error('Error previewing document:', error);
      toast.error("Erreur lors du chargement de l'aperçu");
    }
  };

  const getSuiviBadge = (hab) => {
    if (!hab.suivi_effectif || hab.suivi_effectif === 'ok') {
      if (hab.suivi_statut === 'renouvelee') {
        return <Badge variant="outline" className="text-emerald-700 border-emerald-300 text-[11px]">Renouvelée</Badge>;
      }
      return null;
    }
    if (hab.suivi_effectif === 'en_cours') {
      const datePart = hab.suivi_date_prevue ? ` – ${hab.suivi_en_retard ? 'prévue le' : 'le'} ${formatDateShort(hab.suivi_date_prevue)}` : '';
      return (
        <Badge variant="outline" className={`text-[11px] ${hab.suivi_en_retard ? 'text-red-700 border-red-300' : 'text-blue-700 border-blue-300'}`}>
          Action en cours{datePart}{hab.suivi_en_retard ? ' (dépassée)' : ''}
        </Badge>
      );
    }
    return <Badge variant="outline" className="text-amber-700 border-amber-300 text-[11px]">À traiter</Badge>;
  };

  const getStatusBadge = (statut) => {
    switch (statut) {
      case 'valide':
        return <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle className="w-3 h-3 mr-1" />Valide</Badge>;
      case 'alerte':
        return <Badge className="bg-amber-100 text-amber-800"><Clock className="w-3 h-3 mr-1" />À renouveler</Badge>;
      case 'expire':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Expiré</Badge>;
      default:
        return <Badge>{statut}</Badge>;
    }
  };

  const getTypeIcon = (type) => {
    const typeInfo = habilitationTypes.find(t => t.value === type);
    if (!typeInfo) return null;
    const Icon = typeInfo.icon;
    return <Icon className={`w-5 h-5 ${typeInfo.color}`} />;
  };

  const matchesSuiviFilter = (h) => {
    if (filterSuivi === 'all') return true;
    if (filterSuivi === 'traitees') return h.suivi_statut === 'renouvelee' && h.suivi_effectif === 'ok';
    return h.suivi_effectif === filterSuivi;
  };

  const filteredHabilitations = habilitations.filter(h => {
    const matchesSearch = h.employee_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || h.type_habilitation === filterType;
    const matchesStatus = filterStatus === 'all' || h.statut === filterStatus;
    return matchesSearch && matchesType && matchesStatus && matchesSuiviFilter(h);
  });

  const stats = {
    total: habilitations.length,
    valides: habilitations.filter(h => h.statut === 'valide').length,
    alertes: habilitations.filter(h => h.statut === 'alerte').length,
    expires: habilitations.filter(h => h.statut === 'expire').length
  };

  const suiviCounts = {
    a_traiter: habilitations.filter(h => h.suivi_effectif === 'a_traiter').length,
    en_cours: habilitations.filter(h => h.suivi_effectif === 'en_cours').length,
    traitees: habilitations.filter(h => h.suivi_statut === 'renouvelee' && h.suivi_effectif === 'ok').length
  };

  const suiviChips = [
    { value: 'a_traiter', label: 'À traiter', count: suiviCounts.a_traiter, active: 'bg-amber-100 text-amber-800 border-amber-300' },
    { value: 'en_cours', label: 'En cours', count: suiviCounts.en_cours, active: 'bg-blue-100 text-blue-800 border-blue-300' },
    { value: 'traitees', label: 'Traitées', count: suiviCounts.traitees, active: 'bg-emerald-100 text-emerald-800 border-emerald-300' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="habilitations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Habilitations</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Certifications, formations obligatoires, visites médicales
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F5156] hover:bg-[#0d464a]" data-testid="add-habilitation-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle habilitation
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingHabilitation ? 'Modifier l\'habilitation' : 'Nouvelle habilitation'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Employé *</Label>
                  <Select 
                    value={formData.employee_id} 
                    onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                  >
                    <SelectTrigger data-testid="hab-employee-select">
                      <SelectValue placeholder="Sélectionner un employé" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.prenom} {emp.nom} - {emp.poste}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Type d'habilitation *</Label>
                  <Select 
                    value={formData.type_habilitation} 
                    onValueChange={(value) => setFormData({ ...formData, type_habilitation: value })}
                  >
                    <SelectTrigger data-testid="hab-type-select">
                      <SelectValue placeholder="Sélectionner un type" />
                    </SelectTrigger>
                    <SelectContent>
                      {habilitationTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className={`w-4 h-4 ${type.color}`} />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {formData.type_habilitation === 'permis' && (
                  <div className="space-y-2">
                    <Label>Catégorie de permis</Label>
                    <Select 
                      value={formData.categorie} 
                      onValueChange={(value) => setFormData({ ...formData, categorie: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {permisCategories.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {formData.type_habilitation?.startsWith('caces') && (
                  <div className="space-y-2">
                    <Label>Catégorie CACES</Label>
                    <Input
                      value={formData.categorie}
                      onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                      placeholder="Ex: A, B1, C1..."
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Numéro</Label>
                  <Input
                    value={formData.numero}
                    onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                    placeholder="Numéro du document"
                    data-testid="hab-numero-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date d'obtention *</Label>
                    <Input
                      type="date"
                      value={formData.date_obtention}
                      onChange={(e) => setFormData({ ...formData, date_obtention: e.target.value })}
                      required
                      data-testid="hab-date-obtention-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date d'expiration *</Label>
                    <Input
                      type="date"
                      value={formData.date_expiration}
                      onChange={(e) => setFormData({ ...formData, date_expiration: e.target.value })}
                      required
                      data-testid="hab-date-expiration-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Organisme</Label>
                  <Input
                    value={formData.organisme}
                    onChange={(e) => setFormData({ ...formData, organisme: e.target.value })}
                    placeholder="Organisme de formation/certification"
                    data-testid="hab-organisme-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-[#0F5156] hover:bg-[#0d464a]" data-testid="hab-submit-btn">
                    {editingHabilitation ? 'Enregistrer' : 'Créer'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800 dark:text-amber-400">
                  {alerts.length} habilitation(s) à renouveler ou expirée(s)
                </h3>
                <div className="mt-2 space-y-1">
                  {alerts.slice(0, 3).map((alert, i) => (
                    <p key={i} className="text-sm text-amber-700 dark:text-amber-300">
                      • {alert.employee_name} - {habilitationTypes.find(t => t.value === alert.type_habilitation)?.label || alert.type_habilitation}
                      {alert.jours_restants > 0 ? ` (expire dans ${alert.jours_restants} jours)` : ' (expiré)'}
                    </p>
                  ))}
                  {alerts.length > 3 && (
                    <p className="text-sm text-amber-600">... et {alerts.length - 3} autre(s)</p>
                  )}
                </div>
                {/* Suivi du renouvellement : filtres rapides */}
                <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="hab-suivi-chips">
                  <span className="text-xs text-amber-700 dark:text-amber-300">Suivi :</span>
                  {suiviChips.map((chip) => (
                    <button
                      key={chip.value}
                      type="button"
                      onClick={() => setFilterSuivi(filterSuivi === chip.value ? 'all' : chip.value)}
                      className={`px-3 py-1 rounded-full border text-xs font-medium transition-colors ${filterSuivi === chip.value ? chip.active : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700'}`}
                      data-testid={`hab-suivi-chip-${chip.value}`}
                    >
                      {chip.label} ({chip.count})
                    </button>
                  ))}
                  {filterSuivi !== 'all' && (
                    <button type="button" onClick={() => setFilterSuivi('all')} className="text-xs text-slate-500 underline">
                      Tout afficher
                    </button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Valides</p>
                <p className="text-2xl font-bold text-emerald-500">{stats.valides}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">À renouveler</p>
                <p className="text-2xl font-bold text-amber-500">{stats.alertes}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Expirées</p>
                <p className="text-2xl font-bold text-red-500">{stats.expires}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par employé..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="hab-search-input"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-48" data-testid="hab-filter-type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                {habilitationTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-40" data-testid="hab-filter-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="valide">Valides</SelectItem>
                <SelectItem value="alerte">À renouveler</SelectItem>
                <SelectItem value="expire">Expirées</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-header">Employé</TableHead>
                  <TableHead className="table-header">Type</TableHead>
                  <TableHead className="table-header hidden md:table-cell">Numéro</TableHead>
                  <TableHead className="table-header hidden lg:table-cell">Organisme</TableHead>
                  <TableHead className="table-header">Expiration</TableHead>
                  <TableHead className="table-header">Statut</TableHead>
                  <TableHead className="table-header text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredHabilitations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      Aucune habilitation trouvée
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredHabilitations.map((hab) => (
                    <TableRow key={hab.id} className="table-row" data-testid={`hab-row-${hab.id}`}>
                      <TableCell className="table-cell">
                        <p className="font-medium">{hab.employee_name}</p>
                      </TableCell>
                      <TableCell className="table-cell">
                        <div className="flex items-center gap-2">
                          {getTypeIcon(hab.type_habilitation)}
                          <span>
                            {habilitationTypes.find(t => t.value === hab.type_habilitation)?.label || hab.type_habilitation}
                            {hab.categorie && <span className="text-slate-500 ml-1">({hab.categorie})</span>}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="table-cell hidden md:table-cell">
                        {hab.numero || '-'}
                      </TableCell>
                      <TableCell className="table-cell hidden lg:table-cell">
                        {hab.organisme || '-'}
                      </TableCell>
                      <TableCell className="table-cell">
                        <div>
                          <p>{formatDateShort(hab.date_expiration)}</p>
                          <p className={`text-xs ${hab.jours_restants < 0 ? 'text-red-500' : hab.jours_restants < 90 ? 'text-amber-500' : 'text-slate-400'}`}>
                            {hab.jours_restants > 0 ? `${hab.jours_restants} jours` : 'Expiré'}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="table-cell">
                        <div className="flex flex-col items-start gap-1">
                          {getStatusBadge(hab.statut)}
                          {getSuiviBadge(hab)}
                        </div>
                      </TableCell>
                      <TableCell className="table-cell text-right">
                        <div className="flex items-center justify-end gap-1">
                          {hab.documents_count > 0 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title={`Aperçu du document (${hab.documents_count})`}
                              onClick={() => handlePreviewRow(hab)}
                              data-testid={`hab-preview-${hab.id}`}
                            >
                              <Eye className="w-4 h-4 text-[#0F5156]" />
                            </Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEdit(hab)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openRenew(hab)}>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Renouveler
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openSuivi(hab)}>
                                <ClipboardCheck className="w-4 h-4 mr-2" />
                                Suivi
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setHistoryHab(hab)}>
                                <History className="w-4 h-4 mr-2" />
                                Historique{hab.historique_count > 0 ? ` (${hab.historique_count})` : ''}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                setSelectedHabForDoc(hab);
                                setIsDocDialogOpen(true);
                              }}>
                                <Upload className="w-4 h-4 mr-2" />
                                Joindre document{hab.documents_count > 0 ? ` (${hab.documents_count})` : ''}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(hab.id)} className="text-red-600">
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Délais d'alerte par type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {habilitationTypes.map((type) => {
              const Icon = type.icon;
              return (
                <div key={type.value} className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${type.color}`} />
                  <span>{type.label}: {type.alert_days} jours</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Dialog Documents Habilitation */}
      <Dialog open={isDocDialogOpen} onOpenChange={setIsDocDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Documents - {selectedHabForDoc ? (() => {
                const emp = employees.find(e => e.id === selectedHabForDoc.employee_id);
                const typeName = habilitationTypes.find(t => t.value === selectedHabForDoc.type_habilitation)?.label || selectedHabForDoc.type_habilitation;
                return `${emp ? `${emp.prenom} ${emp.nom}` : ''} - ${typeName}`;
              })() : ''}
            </DialogTitle>
          </DialogHeader>
          {selectedHabForDoc && (
            <HabilitationDocuments
              habilitationId={selectedHabForDoc.id}
              employeeId={selectedHabForDoc.employee_id}
              employeeName={(() => {
                const emp = employees.find(e => e.id === selectedHabForDoc.employee_id);
                return emp ? `${emp.prenom} ${emp.nom}` : '';
              })()}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Renouvellement */}
      <Dialog open={!!renewHab} onOpenChange={(open) => { if (!open) setRenewHab(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Renouveler – {renewHab ? `${renewHab.employee_name || ''} · ${habilitationTypes.find(t => t.value === renewHab.type_habilitation)?.label || renewHab.type_habilitation}` : ''}
            </DialogTitle>
          </DialogHeader>
          {renewHab && (
            <form onSubmit={handleRenew} className="space-y-4 py-2">
              <p className="text-sm text-slate-500">
                Dates actuelles : {formatDateShort(renewHab.date_obtention)} → {formatDateShort(renewHab.date_expiration)}.
                Elles seront conservées dans l'historique.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nouvelle date d'obtention *</Label>
                  <Input type="date" value={renewForm.date_obtention} onChange={(e) => setRenewForm({ ...renewForm, date_obtention: e.target.value })} required data-testid="renew-date-obtention" />
                </div>
                <div className="space-y-2">
                  <Label>Nouvelle date d'expiration *</Label>
                  <Input type="date" value={renewForm.date_expiration} onChange={(e) => setRenewForm({ ...renewForm, date_expiration: e.target.value })} required data-testid="renew-date-expiration" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Organisme</Label>
                <Input value={renewForm.organisme} onChange={(e) => setRenewForm({ ...renewForm, organisme: e.target.value })} placeholder="Organisme de formation/certification" />
              </div>
              <div className="space-y-2">
                <Label>Commentaire</Label>
                <Textarea value={renewForm.commentaire} onChange={(e) => setRenewForm({ ...renewForm, commentaire: e.target.value })} placeholder="Précisions sur le renouvellement" rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Justificatif (PDF, JPG ou PNG, max 10MB) – optionnel</Label>
                <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleRenewFileSelect} />
                {renewFile && <p className="text-xs text-slate-500">{renewFile.filename}</p>}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setRenewHab(null)}>Annuler</Button>
                <Button type="submit" className="bg-[#0F5156] hover:bg-[#0d464a]" disabled={renewSaving} data-testid="renew-submit-btn">
                  {renewSaving ? 'Enregistrement...' : 'Renouveler'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Suivi */}
      <Dialog open={!!suiviHab} onOpenChange={(open) => { if (!open) setSuiviHab(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Suivi – {suiviHab ? `${suiviHab.employee_name || ''} · ${habilitationTypes.find(t => t.value === suiviHab.type_habilitation)?.label || suiviHab.type_habilitation}` : ''}
            </DialogTitle>
          </DialogHeader>
          {suiviHab && (
            <form onSubmit={handleSuivi} className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Statut du suivi</Label>
                <Select value={suiviForm.suivi_statut} onValueChange={(value) => setSuiviForm({ ...suiviForm, suivi_statut: value })}>
                  <SelectTrigger data-testid="suivi-statut-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="a_traiter">{SUIVI_LABELS.a_traiter}</SelectItem>
                    <SelectItem value="en_cours">{SUIVI_LABELS.en_cours}</SelectItem>
                    <SelectItem value="renouvelee">{SUIVI_LABELS.renouvelee}</SelectItem>
                  </SelectContent>
                </Select>
                {suiviForm.suivi_statut === 'renouvelee' && (
                  <p className="text-xs text-slate-500">Pour mettre à jour les dates, utilisez plutôt l'action « Renouveler ».</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Date prévue (visite, formation...)</Label>
                <Input type="date" value={suiviForm.suivi_date_prevue} onChange={(e) => setSuiviForm({ ...suiviForm, suivi_date_prevue: e.target.value })} data-testid="suivi-date-prevue" />
                {suiviForm.suivi_statut === 'en_cours' && (
                  <p className="text-xs text-slate-500">Les alertes sont suspendues jusqu'à cette date.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Commentaire</Label>
                <Textarea value={suiviForm.suivi_commentaire} onChange={(e) => setSuiviForm({ ...suiviForm, suivi_commentaire: e.target.value })} rows={2} />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setSuiviHab(null)}>Annuler</Button>
                <Button type="submit" className="bg-[#0F5156] hover:bg-[#0d464a]" data-testid="suivi-submit-btn">Enregistrer</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Historique */}
      <Dialog open={!!historyHab} onOpenChange={(open) => { if (!open) setHistoryHab(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Historique – {historyHab ? `${historyHab.employee_name || ''} · ${habilitationTypes.find(t => t.value === historyHab.type_habilitation)?.label || historyHab.type_habilitation}` : ''}
            </DialogTitle>
          </DialogHeader>
          {historyHab && (
            <div className="space-y-3 py-2">
              <div className="p-3 rounded-lg border bg-slate-50 dark:bg-slate-800/50 text-sm">
                <p className="font-medium">Période actuelle</p>
                <p>{formatDateShort(historyHab.date_obtention)} → {formatDateShort(historyHab.date_expiration)}{historyHab.organisme ? ` · ${historyHab.organisme}` : ''}</p>
                {historyHab.suivi_date_renouvellement && (
                  <p className="text-xs text-slate-500">Renouvelée le {formatDateShort(historyHab.suivi_date_renouvellement.slice(0, 10))}</p>
                )}
              </div>
              {(historyHab.historique || []).length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Aucun renouvellement enregistré</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {[...historyHab.historique].reverse().map((entry, i) => (
                    <div key={i} className="p-3 rounded-lg border text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{HISTORY_MOTIF_LABELS[entry.motif] || entry.motif || 'Modification'}</span>
                        <span className="text-xs text-slate-500">{entry.remplacee_le ? formatDateShort(entry.remplacee_le.slice(0, 10)) : ''}</span>
                      </div>
                      <p>{formatDateShort(entry.date_obtention)} → {formatDateShort(entry.date_expiration)}{entry.organisme ? ` · ${entry.organisme}` : ''}</p>
                      {entry.commentaire && <p className="text-xs text-slate-500 mt-1">{entry.commentaire}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Aperçu document (A-6) */}
      <DocumentPreviewDialog
        preview={previewDoc}
        onClose={() => setPreviewDoc(null)}
        onDownload={() => previewDoc?.sourceDoc && downloadHabilitationDocument(previewDoc.sourceDoc.id, previewDoc.sourceDoc.filename).catch(() => toast.error('Erreur lors du téléchargement'))}
      />
    </div>
  );
};

export default Habilitations;
